/**
 * /auth/continue — the page magic-link emails land on.
 *
 * The one-time token is consumed by a POST from the button below, never by the
 * page load itself: email security scanners prefetch GET links, which used to
 * burn the token before the user could click it.
 */

import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { ArrowRight, CircleAlert, Ticket } from 'lucide-react';
import { AuthLayout } from '../components/layout';
import { Alert, Button } from '../components/ui';
import { api, ApiError } from '../lib/api';
import { useSessionStore } from '../stores/session';

export function MagicContinuePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const redirect = searchParams.get('redirect') ?? undefined;
  const navigate = useNavigate();
  const refresh = useSessionStore((state) => state.refresh);

  const verify = useMutation({
    mutationFn: () => api.auth.verifyMagicLink({ token: token as string, redirect }),
    onSuccess: async (response) => {
      await refresh();
      navigate(response.redirect || '/', { replace: true });
    },
  });

  if (!token) {
    return (
      <AuthLayout
        title="Incomplete sign-in link"
        subtitle="This page needs the token that came with your sign-in email."
      >
        <Alert tone="warning" title="Open the link from your email" icon={<Ticket />}>
          It looks like part of the link was trimmed when it was copied. Request a fresh one from
          the{' '}
          <Link to="/login" className="font-medium text-accent hover:underline">
            login page
          </Link>
          .
        </Alert>
      </AuthLayout>
    );
  }

  const error = verify.error instanceof ApiError ? verify.error : null;

  return (
    <AuthLayout
      title="Sign in to Murmur"
      subtitle="One more click — this confirms it is really you opening the link."
      footer={
        <>
          Wrong account?{' '}
          <Link to="/login" className="font-medium text-accent hover:underline">
            Back to login
          </Link>
        </>
      }
    >
      <div className="space-y-4">
        {error && (
          <Alert tone="danger" title="This link did not work" icon={<CircleAlert />}>
            {error.message}{' '}
            <Link to="/login" className="font-medium text-accent hover:underline">
              Request a new link
            </Link>
            .
          </Alert>
        )}
        <Button
          size="lg"
          fullWidth
          loading={verify.isPending}
          loadingLabel="Signing you in"
          rightIcon={<ArrowRight className="size-4" />}
          onClick={() => verify.mutate()}
        >
          Continue to Murmur
        </Button>
        <p className="text-center text-xs text-subtle">
          Sign-in links work once and expire 15 minutes after they were requested.
        </p>
      </div>
    </AuthLayout>
  );
}

export default MagicContinuePage;
